<template>
  <div v-if="isShowing" :style="{ height: `${spacerHeight}px` }">
    <section
      ref="card"
      class="retake-finish"
      :class="{ 'retake-finish--compact': isOfferingFinish }"
      :style="{ bottom: `${bottomOffset}px` }"
      data-testid="retake-finish-card"
    >
      <ds-button
        v-if="isOfferingFinish"
        variant="secondary"
        size="touch"
        data-testid="retake-finish-button"
        @click="openFinish"
      >
        {{ $t('finishButton') }}
      </ds-button>
      <template v-else>
        <h2 class="retake-finish__title">
          {{ $t('retakeFinished') }}
        </h2>
        <p v-if="points !== null" class="retake-finish__points" data-testid="retake-finish-points">
          {{ $t('retakeFinishedPoints', { msg: formattedPoints }) }}
        </p>
        <div class="retake-finish__actions">
          <ds-button
            variant="primary"
            size="touch"
            data-testid="replay-play-again-button"
            @click="playAgain"
          >
            {{ $t('replayPlayAgainButton') }}
          </ds-button>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Sentry, { featureTags } from '@/3rd-parties/sentry'
import util from '../../util'
import storage from '../../services/storage'
import Audience from '../../classes/audience'
import EventBus from '../../eventBus'
import { loadAudienceDesignSystem } from '../../utils/designSystemLoader'
import DsButton from './RetakeDsButton'

const POINTS_KEY = 'retakePointsBySlide'
const FIXED_BOTTOM_SELECTORS = ['.quiz-result-container', '.audience-pacing-navigation']
const CARD_GAP = 16
const RESPONSE_SLIDE_TYPES = [
  'multipleChoice', 'imageChoice', 'scale', 'wordCloud', 'openEnded', 'ideas', 'question',
  'ranking', 'survey', 'pinOnImage', 'ideaBoard',
]

export default {
  components: { DsButton },
  data: () => ({
    pointsBySlide: {}, answeredSlideId: null, finishOpenedSlideId: null, bottomOffset: 0, spacerHeight: 0,
  }),
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      activeTabView: (state) => state.activeTabView,
      audienceId: (state) => state.audienceId,
    }),
    ...mapGetters(['activeActivity', 'isQuizSlide']),
    ...mapGetters('quiz', ['quizResultBySlide']),
    ...mapGetters('replay', ['isRetakePlayer']),
    ...mapGetters('slideProgress', ['isLastVisibleSlide', 'visibleSlides']),
    ...mapGetters('embedApp', ['customSlideTypeByType']),
    isWaitingForLastAnswer() {
      const slide = this.activeActivity
      return !!slide && this.isQuizSlide(slide)
        && this.answeredSlideId !== slide.id
        && !this.quizResultBySlide(slide.id)
        && !this.hasValue(this.pointsBySlide[slide.id])
    },
    isOfferingFinish() {
      const slide = this.activeActivity
      if (!slide || this.isQuizSlide(slide) || this.finishOpenedSlideId === slide.id) return false
      return RESPONSE_SLIDE_TYPES.includes(slide.type) || !!this.customSlideTypeByType(slide.type)
    },
    isShowing() {
      return !!this.presentation
        && this.isRetakePlayer
        && this.isLastVisibleSlide
        && this.activeTabView === 'slide'
        && !this.isWaitingForLastAnswer
    },
    points() {
      const quizSlides = (this.visibleSlides || []).filter((slide) => this.isQuizSlide(slide))
      if (!quizSlides.length) return null
      return quizSlides.reduce((total, slide) => total
        + (this.quizResultBySlide(slide.id)?.point ?? this.pointsBySlide[slide.id] ?? 0), 0)
    },
    formattedPoints() {
      return this.points === null ? '' : String(util.formatNumber(this.points))
    },
    storePointsBySlide() {
      return (this.visibleSlides || []).reduce((pointsBySlide, slide) => {
        const point = this.isQuizSlide(slide) ? this.quizResultBySlide(slide.id)?.point : null
        return this.hasValue(point) ? { ...pointsBySlide, [slide.id]: point } : pointsBySlide
      }, {})
    },
  },
  watch: {
    'presentation.id': {
      immediate: true,
      handler(presentationId) {
        if (presentationId && this.isRetakePlayer) this.loadSavedPoints()
      },
    },
    // A scored plugin's result lives only in memory and never reaches PickAnswerContent's settled event.
    storePointsBySlide: {
      immediate: true,
      handler(pointsBySlide) {
        if (Object.keys(pointsBySlide).length) this.savePoints(pointsBySlide)
      },
    },
    isShowing: {
      immediate: true,
      handler(showing) {
        this.stopPlacing()
        if (!showing) return
        loadAudienceDesignSystem().catch((error) => {
          Sentry.captureException(error, {
            extra: { message: 'load design system failed' },
            tags: featureTags({ featureFlag: 'AHA-49118-replay', featureName: 'retake-finish-card', taskId: 'SEPA-22' }),
          })
        })
        this.loadSavedPoints()
        window.addEventListener('resize', this.placeAboveFixedBars)
        // The quiz result banner slides in after the card shows, so its top is only final then.
        document.addEventListener('animationend', this.placeAboveFixedBars)
        this.$nextTick(this.placeAboveFixedBars)
      },
    },
  },
  created() {
    EventBus.$on('retake-answer-settled', this.rememberAnswer)
  },
  beforeDestroy() {
    EventBus.$off('retake-answer-settled', this.rememberAnswer)
    this.stopPlacing()
  },
  methods: {
    stopPlacing() {
      window.removeEventListener('resize', this.placeAboveFixedBars)
      document.removeEventListener('animationend', this.placeAboveFixedBars)
    },
    placeAboveFixedBars() {
      const { card } = this.$refs
      if (!card) return
      const tops = FIXED_BOTTOM_SELECTORS
        .map((selector) => document.querySelector(selector))
        .filter(Boolean)
        .map((bar) => bar.getBoundingClientRect().top)
      const lowestFreeY = Math.min(window.innerHeight, ...tops)
      this.bottomOffset = window.innerHeight - lowestFreeY + CARD_GAP
      this.spacerHeight = card.offsetHeight + this.bottomOffset + CARD_GAP
    },
    hasValue(point) {
      return point !== null && point !== undefined
    },
    savedPoints() {
      return storage.getValueByPresentation(this.presentation.id, POINTS_KEY, {}) || {}
    },
    loadSavedPoints() {
      this.pointsBySlide = { ...this.savedPoints(), ...this.pointsBySlide }
    },
    savePoints(points) {
      if (!this.isRetakePlayer || !this.presentation) return
      this.pointsBySlide = { ...this.savedPoints(), ...this.pointsBySlide, ...points }
      storage.updateValueByPresentation(this.presentation.id, POINTS_KEY, this.pointsBySlide)
    },
    openFinish() {
      this.finishOpenedSlideId = this.activeActivity?.id
      this.$nextTick(this.placeAboveFixedBars)
    },
    rememberAnswer(slideId, point) {
      if (!this.isRetakePlayer) return
      this.answeredSlideId = slideId
      if (this.hasValue(point)) this.savePoints({ [slideId]: point })
    },
    playAgain() {
      util.ga4Track('click_replay_play_again', { presentationId: this.presentation.id })
      storage.clearValueByPresentation(this.presentation.id)
      storage.updateValueByPresentation(this.presentation.id, 'audienceId', Audience.generateId())
      window.location.reload()
    },
  },
}
</script>

<style lang="scss" scoped>
// Above the quiz result banner, below the utility menu (z-index 50).
.retake-finish {
  position: fixed;
  left: 50%;
  z-index: 40;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 526px;
  padding: theme('spacing.4');
  box-sizing: border-box;
  background-color: theme('colors.base.0');
  border: 1px solid var(--aha-border-default);
  border-radius: theme('borderRadius.xl');
  color: theme('colors.ds.base');
  text-align: center;
}

.retake-finish--compact {
  width: max-content;
  padding: 0;
  background-color: transparent;
  border: 0;
}

.retake-finish__title {
  margin: 0;
  font-size: theme('fontSize.xl');
  font-weight: 600;
}

.retake-finish__points {
  margin: theme('spacing.1') 0 0;
  font-size: theme('fontSize.base');
}

.retake-finish__actions {
  display: flex;
  flex-direction: column;
  gap: theme('spacing.2');
  margin-top: theme('spacing.4');
}
</style>
